import { HighlightedText } from "components/HighlightedText";
import { AbstractLoading } from "components/Loading/AbstractLoading";
import { Paper } from "helpers/api";
import useLabels from "hooks/useLabels";
import usePaperDetails from "hooks/usePaperDetails";
import { useEffect, useState } from "react";
import FloatingQuoteSelector from "./FloatingQuoteSelector";
import { Text } from "design_system";
import { PaperQuote } from "store/slices/citedPapers";
import PaperMetadata from "components/PaperMetadata/PaperMetadata";

type PaperAbstractTabProps = {
  paper: Paper;
  selectedQuoteIndex?: number;
  quotes?: PaperQuote[];
};

function PaperAbstractTab({
  paper,
  selectedQuoteIndex,
  quotes = [],
}: PaperAbstractTabProps) {
  const [detailsPageLabels] = useLabels("screens.details");
  const { data: paperDetails, isLoading } = usePaperDetails(paper.paper_id);

  // Local state for currently selected quote index
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState<
    number | undefined
  >(selectedQuoteIndex);

  // Update currentQuoteIndex when selectedQuoteIndex prop changes
  useEffect(() => {
    setCurrentQuoteIndex(selectedQuoteIndex);
  }, [selectedQuoteIndex]);

  const abstractText =
    paperDetails?.abstract === ""
      ? detailsPageLabels["no-abstract"]
      : (paperDetails?.abstract ?? detailsPageLabels["abstract-removed"]);

  const selectedQuoteObj =
    currentQuoteIndex !== undefined ? quotes[currentQuoteIndex] : undefined;
  const currentExcerpt = selectedQuoteObj?.quote;

  return (
    <div className="overflow-y-auto p-4 h-full">
      {isLoading ? (
        <AbstractLoading />
      ) : (
        <div className="relative max-w-none prose">
          <Text weight="bold" size="lg" className="mb-3">
            {paper.title}
          </Text>
          <PaperMetadata paper={paper} useTwoRowLayout />
          <hr className="my-3! border-border-base w-full" />
          <Text className="mb-3 whitespace-pre-wrap">
            <HighlightedText
              text={abstractText}
              highlight={currentExcerpt}
              highlightClassName="bg-[#068EF133]"
            />
          </Text>
        </div>
      )}

      {/* Floating QuoteSelector */}
      {quotes.length > 0 && (
        <FloatingQuoteSelector
          quotes={quotes}
          selectedQuote={currentQuoteIndex}
          onSelectQuote={setCurrentQuoteIndex}
          citation={1} // TODO(Jimmy): Pass in real citation number once analyses can have more tha one quoted paper at a time.
        />
      )}
    </div>
  );
}

export default PaperAbstractTab;
